/**
 * On-chain crypto (`crypto_onchain`): the pure rules of the inline payment and the
 * `CryptoOnchainCheckout` flow. Same behaviour as the hosted (Studio) checkout's crypto panel:
 * default network and coin, status phases, quote countdown, polling until paid.
 */
import type {
  CryptoOnchainCheckoutOptions,
  CryptoOnchainCheckoutState,
  CryptoOnchainNetwork,
  CryptoOnchainOptionsResult,
  CryptoOnchainPaymentResult,
  CryptoOnchainPhase,
  CryptoOnchainSelection,
  CryptoOnchainStatus,
  CryptoOnchainStatusResult,
} from '../types.js';

/** `paymentSetupConfig` key and payment method name of on-chain crypto. */
export const CRYPTO_ONCHAIN_PAYMENT_METHOD = 'crypto_onchain';

/** How often the checkout polls the status, in milliseconds. */
export const CRYPTO_ONCHAIN_STATUS_POLL_MS = 5000;

/** Phases after which polling stops. */
export const CRYPTO_ONCHAIN_FINAL_PHASES: readonly CryptoOnchainPhase[] = ['paid', 'underpaid', 'failed', 'expired'];

/** True for `crypto_onchain` in any spelling a checkout uses (`crypto-onchain`, `crypto_onchain:int_…`). */
export function isCryptoOnchainMethod(method: string | null | undefined): boolean {
  if (!method) return false;
  return method.trim().toLowerCase().replace(/-/g, '_').split(':')[0] === CRYPTO_ONCHAIN_PAYMENT_METHOD;
}

/**
 * The default pick: the first mainnet network (testnets only when nothing else is offered) and its
 * first stablecoin, else its first coin. Null when no network offers a coin.
 */
export function defaultCryptoOnchainSelection(networks: CryptoOnchainNetwork[]): CryptoOnchainSelection | null {
  const usable = networks.filter((network) => network.assets.length > 0);
  const network = usable.find((candidate) => !candidate.testnet) ?? usable[0];
  if (!network) return null;
  const asset = network.assets.find((candidate) => candidate.stable) ?? network.assets[0];
  return { chainId: network.chainId, asset: asset.symbol };
}

/** Switching network keeps the coin when the new network offers it, else takes its default coin. */
export function selectCryptoOnchainNetwork(
  networks: CryptoOnchainNetwork[],
  chainId: number,
  currentAsset: string,
): CryptoOnchainSelection {
  const assets = networks.find((network) => network.chainId === chainId)?.assets ?? [];
  const keep = assets.find((asset) => asset.symbol === currentAsset);
  const fallback = assets.find((asset) => asset.stable) ?? assets[0];
  return { chainId, asset: (keep ?? fallback)?.symbol ?? currentAsset };
}

/** The phase of the latest status (or of the payment as created, before the first poll). */
export function cryptoOnchainPhase(
  status: Pick<CryptoOnchainStatus, 'status' | 'paid' | 'expired'> | null,
): CryptoOnchainPhase {
  if (!status) return 'waiting';
  if (status.paid) return 'paid';
  switch (status.status) {
    case 'detected':
    case 'overpaid':
      return 'confirming';
    case 'underpaid':
      return 'underpaid';
    case 'failed':
      return 'failed';
    case 'expired':
      return 'expired';
    default:
      return status.expired ? 'expired' : 'waiting';
  }
}

/** Whole seconds until `expiresAt`, never negative; 0 for a missing or unparsable date. */
export function cryptoOnchainSecondsLeft(expiresAt: string | null, nowMs: number): number {
  if (!expiresAt) return 0;
  const end = Date.parse(expiresAt);
  if (Number.isNaN(end)) return 0;
  return Math.max(0, Math.floor((end - nowMs) / 1000));
}

/** `m:ss` under an hour, `h:mm:ss` above. */
export function formatCryptoOnchainCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** The three calls the flow needs; `PaymentModule` provides them. */
export interface CryptoOnchainApi {
  getCryptoOnchainOptions(checkoutSessionId: string): Promise<CryptoOnchainOptionsResult>;
  createCryptoOnchainPayment(opts: { checkoutSessionId: string; chainId: number; asset: string }): Promise<CryptoOnchainPaymentResult>;
  getCryptoOnchainStatus(opts: { checkoutSessionId: string; cryptoOnchainPaymentId: string }): Promise<CryptoOnchainStatusResult>;
}

const UNAVAILABLE = 'Crypto payments are not available right now. Choose another payment method.';

/**
 * The inline crypto payment of one checkout session: load the networks and coins, keep the pick,
 * create (or re-quote) the payment, poll it until crypto-api confirms it and call `onPaid` once.
 * Create it with `tagada.payment.createCryptoOnchainCheckout()`.
 */
export class CryptoOnchainCheckout {
  private state: CryptoOnchainCheckoutState = {
    options: 'idle',
    networks: [],
    selection: null,
    payment: null,
    paymentStatus: null,
    isCreating: false,
    error: null,
  };

  private readonly listeners = new Set<(state: CryptoOnchainCheckoutState) => void>();
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private paidNotified = false;

  constructor(
    private readonly api: CryptoOnchainApi,
    private readonly options: CryptoOnchainCheckoutOptions,
  ) {
    this.subscribe = this.subscribe.bind(this);
    this.getState = this.getState.bind(this);
  }

  getState(): CryptoOnchainCheckoutState {
    return this.state;
  }

  /** Listens to every state change; returns the unsubscribe function. */
  subscribe(listener: (state: CryptoOnchainCheckoutState) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** What to show: the polled phase, or `expired` once an unpaid quote ran out (renew with `pay()`). */
  getPhase(nowMs: number = Date.now()): CryptoOnchainPhase {
    const { payment, paymentStatus } = this.state;
    const phase = cryptoOnchainPhase(paymentStatus ?? (payment ? { status: payment.status, paid: false, expired: false } : null));
    const quoteOver = payment !== null && phase === 'waiting' && cryptoOnchainSecondsLeft(payment.expiresAt, nowMs) === 0;
    return quoteOver ? 'expired' : phase;
  }

  /** Whole seconds left on the current quote. */
  getSecondsLeft(nowMs: number = Date.now()): number {
    return cryptoOnchainSecondsLeft(this.state.payment?.expiresAt ?? null, nowMs);
  }

  /** Loads the networks and coins and picks the default network and coin. */
  async loadOptions(): Promise<CryptoOnchainCheckoutState> {
    this.setState({ options: 'loading', error: null });
    try {
      const result = await this.api.getCryptoOnchainOptions(this.options.checkoutSessionId);
      if (!result.ok) {
        this.setState({ options: 'unavailable', networks: [], selection: null, error: result.message || UNAVAILABLE });
        return this.state;
      }
      const selection = this.state.selection ?? defaultCryptoOnchainSelection(result.networks);
      this.setState(
        selection
          ? { options: 'ready', networks: result.networks, selection }
          : { options: 'unavailable', networks: result.networks, selection: null, error: UNAVAILABLE },
      );
    } catch (error: unknown) {
      this.setState({ options: 'unavailable', error: messageOf(error) });
    }
    return this.state;
  }

  /** Changes the pick. A new network keeps the coin when it offers it, else takes its default coin. */
  select(next: { chainId?: number; asset?: string }): void {
    const current = this.state.selection;
    if (next.chainId !== undefined && next.chainId !== current?.chainId) {
      this.setState({ selection: selectCryptoOnchainNetwork(this.state.networks, next.chainId, next.asset ?? current?.asset ?? '') });
      return;
    }
    if (next.asset !== undefined && current) this.setState({ selection: { ...current, asset: next.asset } });
  }

  /**
   * Creates the payment for the pick (or `selection`), or re-quotes it: another coin, or a quote
   * that expired. Save the customer and address on the session first. Starts polling.
   */
  async pay(selection?: CryptoOnchainSelection): Promise<CryptoOnchainPaymentResult> {
    const target = selection ?? this.state.selection;
    if (!target) return { ok: false, code: 'ASSET_NOT_AVAILABLE', message: 'Choose a network and a coin' };
    if (this.state.isCreating) return { ok: false, code: 'CHECKOUT_FAILED', message: 'A payment is already being created' };

    this.setState({ isCreating: true, error: null, selection: target });
    try {
      const result = await this.api.createCryptoOnchainPayment({
        checkoutSessionId: this.options.checkoutSessionId,
        chainId: target.chainId,
        asset: target.asset,
      });
      if (!result.ok) {
        this.setState({ isCreating: false, error: result.message });
        return result;
      }
      this.stopped = false;
      this.setState({ isCreating: false, payment: result.payment, paymentStatus: null });
      this.schedulePoll();
      return result;
    } catch (error: unknown) {
      const message = messageOf(error);
      this.setState({ isCreating: false, error: message });
      return { ok: false, code: 'CHECKOUT_FAILED', message };
    }
  }

  /** Polls the status once now (the timer keeps polling until the payment is final). */
  async refreshStatus(): Promise<CryptoOnchainStatus | null> {
    const payment = this.state.payment;
    if (!payment) return null;
    try {
      const result = await this.api.getCryptoOnchainStatus({
        checkoutSessionId: this.options.checkoutSessionId,
        cryptoOnchainPaymentId: payment.cryptoOnchainPaymentId,
      });
      // A newer quote replaced this payment while the call was in flight.
      if (!result.ok || this.state.payment?.cryptoOnchainPaymentId !== payment.cryptoOnchainPaymentId) return null;
      const status: CryptoOnchainStatus = {
        status: result.status,
        paymentId: result.paymentId,
        orderId: result.orderId,
        confirmations: result.confirmations,
        requiredConfirmations: result.requiredConfirmations,
        txHash: result.txHash,
        explorerTxUrl: result.explorerTxUrl,
        paid: result.paid,
        expired: result.expired,
        redirectUrl: result.redirectUrl,
      };
      this.setState({ paymentStatus: status });
      if (status.paid && !this.paidNotified) {
        this.paidNotified = true;
        this.options.onPaid?.(status);
      }
      return status;
    } catch {
      // The next tick retries; the payment still settles server side.
      return null;
    }
  }

  /** Polls again after `stop()`, while a payment is open (the customer came back to crypto). */
  resume(): void {
    if (!this.state.payment || CRYPTO_ONCHAIN_FINAL_PHASES.includes(this.getPhase())) return;
    this.stopped = false;
    this.schedulePoll();
  }

  /** Stops polling (unmount, or the customer chose another payment method). */
  stop(): void {
    this.stopped = true;
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  private schedulePoll(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = setTimeout(async () => {
      this.pollTimer = null;
      await this.refreshStatus();
      if (!this.stopped && !CRYPTO_ONCHAIN_FINAL_PHASES.includes(this.getPhase())) this.schedulePoll();
    }, this.options.pollIntervalMs ?? CRYPTO_ONCHAIN_STATUS_POLL_MS);
  }

  private setState(patch: Partial<CryptoOnchainCheckoutState>): void {
    this.state = { ...this.state, ...patch };
    this.options.onChange?.(this.state);
    this.listeners.forEach((listener) => listener(this.state));
  }
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : UNAVAILABLE;
}
